import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useStateSession } from '../../../hooks/useAuth';
import { computeEditKeep, computeRegenerateKeep } from './branching';
import { t } from '@lingui/core/macro';
import { translateServerError } from './serverErrors';
import { API_BASE, downloadChatFile } from './downloadFile';
import type { ChatMessage, ChatToolCall, ChatView, Conversation, ChatFile } from './types';

const CHAT_FORBIDDEN_MESSAGE = () =>
  t`AI chat is restricted to admin, providerAdmin or providerUser.`;

/** Generates a collision-free id for optimistic messages. */
function newLocalId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Rows per page of history. */
const HISTORY_PAGE_SIZE = 20;

interface StreamFrame {
  event: string;
  data: Record<string, unknown>;
}

/**
 * Splits a raw SSE buffer into complete frames.
 *
 * The buffer can end mid-frame, so anything after the last blank line is held
 * back until the next read completes it.
 */
function parseFrames(buffer: string): { frames: StreamFrame[]; rest: string } {
  const frames: StreamFrame[] = [];
  let rest = buffer;

  while (true) {
    const boundary = rest.indexOf('\n\n');
    if (boundary === -1) break;

    const block = rest.slice(0, boundary);
    rest = rest.slice(boundary + 2);

    let event = 'message';
    const dataLines: string[] = [];

    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) {
        event = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trim());
      }
    }

    if (dataLines.length === 0) continue;

    try {
      frames.push({ event, data: JSON.parse(dataLines.join('\n')) });
    } catch {
      // Ignore malformed frames rather than killing the stream.
    }
  }

  return { frames, rest };
}

export function useChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState('');

  const token = useSelector((state: any) => state.session.accessToken);
  // The assistant answers in the language the interface is in. The backend
  // falls back to English for anything it does not recognise.
  const localeKey = useSelector((state: any) => state.locale?.localeKey);
  const session = useStateSession();
  const allowed = session?.hasPermission('', 'applications/ai/entities/chat/use');

  // Id of the optimistic user turn, so a failure can roll back exactly that one
  // and leave real history intact.
  const pendingUserIdRef = useRef<string | null>(null);
  // Id of the assistant turn currently being streamed.
  const streamingIdRef = useRef<string | null>(null);
  // Cancels an in-flight request when the user presses Stop.
  const abortControllerRef = useRef<AbortController | null>(null);

  // Mirrors of the paging state, so `fetchConversations` does not have to take
  // the cursor and the search term as dependencies. Those change on every page
  // and keystroke, and this callback is itself a dependency of the send, clear
  // and delete paths.
  const historyCursorRef = useRef<string | null>(null);
  const historySearchRef = useRef('');

  // Counts history requests so a slow earlier one cannot land after a newer one
  // and replace fresh results with stale ones. Typing in the search box starts
  // a request per keystroke, and they do not come back in order.
  const historyRequestRef = useRef(0);

  /**
   * Loads one page of history.
   *
   * `reset` starts again from the first page and replaces what is on screen.
   * Otherwise the page is appended, which is what the cursor is for.
   */
  const fetchConversations = useCallback(
    async (reset = true) => {
      if (!allowed) return;

      const cursor = historyCursorRef.current;

      // Appending with no cursor would refetch page one and stick it on the end
      // of the list, so it is not a thing this is allowed to do.
      if (!reset && !cursor) return;

      const requestId = ++historyRequestRef.current;
      setHistoryLoading(true);

      try {
        const params: Record<string, string | number> = { limit: HISTORY_PAGE_SIZE };
        const term = historySearchRef.current.trim();
        if (!reset && cursor) params.cursor = cursor;
        if (term) params.search = term;

        const response = await axios.get(`${API_BASE}/ai/conversations`, {
          headers: { Authorization: `Bearer ${token}` },
          params,
        });

        if (requestId !== historyRequestRef.current) return;

        const items: Conversation[] = response.data.items || [];
        setConversations((previous) => (reset ? items : [...previous, ...items]));

        // The cursor is only ever consumed by the next "load more", so it is
        // kept in a ref rather than rendered state.
        historyCursorRef.current = response.data.next_cursor ?? null;
        setHistoryHasMore(Boolean(response.data.has_more));
      } catch (err) {
        if (requestId === historyRequestRef.current) {
          console.error('Failed to fetch conversations:', err);
        }
      } finally {
        // Only the newest request owns the spinner, otherwise a superseded one
        // turns it off while the current one is still running.
        if (requestId === historyRequestRef.current) setHistoryLoading(false);
      }
    },
    [token, allowed],
  );

  /** Appends the next page. */
  const loadMoreConversations = useCallback(() => {
    fetchConversations(false);
  }, [fetchConversations]);

  /**
   * Filters history by title.
   *
   * Always a reset: page two of a result set the user cannot see any more is
   * not a continuation of anything, and its cursor would be dropped.
   */
  const searchConversations = useCallback(
    (term: string) => {
      const next = term.trim();
      if (next === historySearchRef.current) return;

      historySearchRef.current = next;
      setHistorySearch(next);
      historyCursorRef.current = null;
      fetchConversations(true);
    },
    [fetchConversations],
  );

  const loadConversation = useCallback(
    async (convId: string) => {
      if (!allowed) return;
      try {
        const response = await axios.get(`${API_BASE}/ai/conversations/${convId}/messages`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const msgs = response.data.items || [];
        setMessages(
          msgs.map((m: any) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            created_at: m.created_at,
            // Restored from the stored reply, so the panels come back on reload
            // instead of only existing for as long as the stream did.
            toolCalls: (m.tool_calls ?? undefined) as ChatToolCall[] | undefined,
            complete: m.complete,
          })),
        );
        setConversationId(convId);
      } catch (err) {
        console.error('Failed to load conversation:', err);
      }
    },
    [token, allowed],
  );

  const sendMessage = useCallback(
    async (content: string, opts?: { conversationIdOverride?: string | null }) => {
      if (!allowed) {
        setError(CHAT_FORBIDDEN_MESSAGE());
        return;
      }
      if (!content.trim() || isLoading) return;

      setIsLoading(true);
      setError(null);

      const userMessage: ChatMessage = {
        id: newLocalId(),
        role: 'user',
        content,
      };

      pendingUserIdRef.current = userMessage.id;
      streamingIdRef.current = null;

      setMessages((prev) => [...prev, userMessage]);

      // Ensure an assistant bubble exists so tokens have somewhere to land.
      const assistantId = newLocalId();
      streamingIdRef.current = assistantId;
      setMessages((prev) => [
        ...prev,
        { id: assistantId, role: 'assistant', content: '', toolCalls: [] },
      ]);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      // Regenerate and edit pass an explicit conversation: both replay into a
      // thread that has just been truncated, so the id we are about to receive
      // is not the one this hook is currently holding.
      const targetConversationId =
        opts && 'conversationIdOverride' in opts
          ? opts.conversationIdOverride
          : conversationId;

      /** Updates the streaming assistant bubble. */
      const updateAssistant = (update: (msg: ChatMessage) => ChatMessage) => {
        setMessages((prev) => prev.map((m) => (m.id === assistantId ? update(m) : m)));
      };

      try {
        const response = await fetch(`${API_BASE}/ai/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            message: content,
            conversation_id: targetConversationId,
            locale: localeKey ?? undefined,
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          // The backend sends a machine-readable `code` alongside its message.
          // The code is what gets translated; the message is the fallback for
          // anything this build does not know about.
          throw new Error(
            translateServerError(errorData.code) ||
              errorData.error ||
              errorData.message ||
              t`Server error: ${response.status}`,
          );
        }

        if (!response.body) {
          throw new Error(t`Streaming is not supported by this browser.`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        let done = false;
        while (!done) {
          const { done: finished, value } = await reader.read();
          if (finished) break;

          buffer += decoder.decode(value, { stream: true });
          const { frames, rest } = parseFrames(buffer);
          buffer = rest;

          for (const frame of frames) {
            switch (frame.event) {
              case 'token':
                updateAssistant((msg) => ({
                  ...msg,
                  content: msg.content + String(frame.data.text ?? ''),
                }));
                break;

              case 'tool_start':
                updateAssistant((msg) => ({
                  ...msg,
                  toolCalls: [
                    ...(msg.toolCalls ?? []),
                    {
                      id: String(frame.data.id),
                      name: String(frame.data.name),
                      arguments: frame.data.arguments,
                      running: true,
                    },
                  ],
                }));
                break;

              case 'tool_end':
                updateAssistant((msg) => ({
                  ...msg,
                  toolCalls: (msg.toolCalls ?? []).map((call) =>
                    call.id === frame.data.id
                      ? {
                          ...call,
                          ok: Boolean(frame.data.ok),
                          summary: String(frame.data.summary ?? ''),
                          running: false,
                        }
                      : call,
                  ),
                }));
                break;

              case 'file':
                // The file belongs to the call that wrote it, so it is
                // attached to that call rather than to the message. That way
                // it is still there when the conversation is reloaded, and it
                // is still there if the reply made several calls.
                updateAssistant((msg) => ({
                  ...msg,
                  toolCalls: (msg.toolCalls ?? []).map((call) =>
                    call.id === frame.data.call_id
                      ? {
                          ...call,
                          file: {
                            id: String(frame.data.id),
                            filename: String(frame.data.filename ?? 'export.csv'),
                            row_count: Number(frame.data.row_count ?? 0),
                            byte_size: Number(frame.data.byte_size ?? 0),
                          },
                        }
                      : call,
                  ),
                }));
                break;

              case 'tool_view':
                // The result of the call, arranged for reading: a table with
                // the ids turned into links, and sometimes a chart. It is
                // attached to the call it belongs to, like the file.
                updateAssistant((msg) => ({
                  ...msg,
                  toolCalls: (msg.toolCalls ?? []).map((call) =>
                    call.id === frame.data.call_id
                      ? { ...call, view: frame.data.view as ChatView }
                      : call,
                  ),
                }));
                break;

              case 'done':
                done = true;
                // The reply ran to the end, so it is not a half answer. Set
                // explicitly rather than left undefined: an aborted turn is
                // marked false, and the two should not rely on different
                // defaults to look different.
                setMessages((prev) =>
                  prev.map((m) => (m.id === assistantId ? { ...m, complete: true } : m)),
                );
                if (frame.data.conversation_id) {
                  setConversationId(String(frame.data.conversation_id));
                }
                fetchConversations();
                break;

              case 'error':
                throw new Error(
                  translateServerError(frame.data.code) ||
                    String(frame.data.message ?? t`Chat failed`),
                );
            }
          }
        }

        // Drop a bubble that never received anything, e.g. an aborted turn.
        // A bubble that made tool calls is kept even without text: a file
        // the assistant produced is an answer in its own right, and dropping
        // the bubble would take the download with it.
        setMessages((prev) =>
          prev.filter(
            (m) =>
              m.id !== assistantId ||
              m.content.length > 0 ||
              (m.toolCalls?.length ?? 0) > 0,
          ),
        );
      } catch (err: any) {
        // A user-initiated abort is not an error.
        if (err?.name === 'AbortError') {
          setMessages((prev) =>
            prev.filter(
              (m) =>
                m.id !== assistantId ||
                m.content.length > 0 ||
                (m.toolCalls?.length ?? 0) > 0,
            ),
          );
        } else {
          console.error('Chat error:', err);
          setError(err.message || t`Failed to send message`);
          setMessages((prev) =>
            prev.filter(
              (m) => m.id !== pendingUserIdRef.current && m.id !== assistantId,
            ),
          );
        }
      } finally {
        pendingUserIdRef.current = null;
        streamingIdRef.current = null;
        abortControllerRef.current = null;
        setIsLoading(false);
      }
    },
    [token, conversationId, isLoading, fetchConversations, allowed, localeKey],
  );

  /** Cancels the in-flight request. */
  const abort = useCallback(() => {
    abortControllerRef.current?.abort();

    // The reply being streamed is now a half answer. Marked here rather than
    // left to the next reload, so it is labelled the same either way.
    const streamingId = streamingIdRef.current;
    if (!streamingId) return;

    setMessages((prev) =>
      prev.map((m) => (m.id === streamingId ? { ...m, complete: false } : m)),
    );
  }, []);

  /** Drops every message after the first `keep` ones, server-side. */
  const truncate = useCallback(
    async (convId: string, keep: number) => {
      const response = await axios.post(
        `${API_BASE}/ai/conversations/${convId}/truncate`,
        { keep },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data?.removed ?? 0;
    },
    [token],
  );

  /**
   * Fetches a file the assistant produced and saves it.
   *
   * The file is fetched with the session token rather than linked to
   * directly: the download endpoint needs the caller's credentials, and a
   * plain link cannot carry them.
   */
  const downloadFile = useCallback(
    async (file: ChatFile) => {
      try {
        await downloadChatFile(file.id, file.filename, token);
      } catch (err: any) {
        console.error('Failed to download the file:', err);
        setError(err?.message || t`Could not download the file.`);
      }
    },
    [token],
  );

  /**
   * Re-asks the most recent question.
   *
   * The last assistant reply is discarded server-side before the question is
   * replayed, so the model answers from the same history rather than seeing two
   * copies of itself and continuing the earlier one.
   */
  const regenerate = useCallback(async () => {
    if (isLoading || !conversationId) return;

    // Drop the trailing assistant turn, keeping everything up to the question.
    const keep = computeRegenerateKeep(messages);
    if (keep === null) return;

    const lastUserMessage = messages[keep];

    const survivors = messages.slice(0, keep);
    setMessages(survivors);

    try {
      await truncate(conversationId, keep);
    } catch (err) {
      console.error('Failed to discard the previous reply:', err);
      // Put the thread back the way it was rather than leaving the UI and the
      // server disagreeing about what the conversation contains.
      setMessages(messages);
      setError(t`Could not regenerate: the previous reply could not be discarded.`);
      return;
    }

    await sendMessage(lastUserMessage.content, { conversationIdOverride: conversationId });
  }, [isLoading, conversationId, messages, truncate, sendMessage]);

  /**
   * Replaces a user question and branches the thread from there.
   *
   * Everything from the edited message onwards is discarded, so the new answer
   * is produced from the history that preceded it rather than arguing with a
   * reply to the old question.
   */
  const editMessage = useCallback(
    async (messageId: string, content: string) => {
      if (isLoading || !conversationId || !content.trim()) return;

      const index = messages.findIndex((m) => m.id === messageId);
      const keep = computeEditKeep(messages, index);
      if (keep === null) return;

      const survivors = messages.slice(0, keep);
      setMessages(survivors);

      try {
        await truncate(conversationId, keep);
      } catch (err) {
        console.error('Failed to branch the conversation:', err);
        setMessages(messages);
        setError(t`Could not edit: the conversation could not be branched.`);
        return;
      }

      await sendMessage(content.trim(), { conversationIdOverride: conversationId });
    },
    [isLoading, conversationId, messages, truncate, sendMessage],
  );

  const clearConversation = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setError(null);
  }, []);

  const deleteConversation = useCallback(
    async (convId: string) => {
      if (!allowed) return;
      try {
        await axios.delete(`${API_BASE}/ai/conversations/${convId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (conversationId === convId) {
          clearConversation();
        }
        fetchConversations();
      } catch (err) {
        console.error('Failed to delete conversation:', err);
      }
    },
    [token, conversationId, clearConversation, fetchConversations, allowed],
  );

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Abort any in-flight stream when the widget unmounts.
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  return {
    messages,
    isLoading,
    error,
    conversationId,
    conversations,
    historyHasMore,
    historyLoading,
    historySearch,
    loadMoreConversations,
    searchConversations,
    sendMessage,
    abort,
    setError,
    regenerate,
    editMessage,
    clearConversation,
    loadConversation,
    deleteConversation,
    fetchConversations,
    downloadFile,
  };
}